import { defineComponent as _defineComponent } from 'vue'
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { computed, watch, ref } from 'vue';

import { useBilling } from '@/components/Billing/Billing.composables';
// @ts-ignore
import BillingMonthlyAnnuallySwitch from '@/components/Billing/BillingMonthlyAnnuallySwitch.vue';
// @ts-ignore
import BillingOveragesTooltip from '@/components/Billing/BillingOveragesTooltip.vue';
// @ts-ignore
import SeeBreakdownButton from '@/components/Billing/SeeBreakdownButton.vue';
import BillingService from '@/components/Billing/billing-service';
import { ANNUAL_PLANS_DISCOUNT, DATE_FORMAT_DISPLAY } from '@/components/Billing/billing-service.const';
import {
	ProductType,
	DiscountType,
	PurchaseDetails,
	SubscriptionStatus,
	ProductInvoicePreview,
	PricePreviewInputParams,
} from '@/components/Billing/billing-types';
// import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { getReadableAmountInDollars, isNullOrUndefined } from '@/utils/helpers';

export default /*#__PURE__*/_defineComponent({
  __name: 'BillingSummaryBar',
  props: {
    newPurchaseDetails: null,
    currentPurchaseDetails: null
  },
  setup(__props: any) {

const props = __props as {
	newPurchaseDetails: PurchaseDetails;
	currentPurchaseDetails: PurchaseDetails;
}

const CUSTOM_PRICE = 'Custom';

const {
	user,
	account,
	subscriptions,
	billingPeriod,
	isAnnuallyPlan,
	usePricingPreview,
	creditsSubscription,
	// isOveragesEnabled,
	automationSubscription,
	teamFeaturesSubscription,
	usePlanById,
	getEntitlements,
	getBillingPreview,
} = useBilling();

const initializeData = async () => {
	try {
		await Promise.all([getEntitlements({})]);
	} catch (error) {
		console.error('Failed to load data');
	}
};
initializeData();


const showErrorMessage = ref(false);
// Account
const discount = ref(0);
const planEndDate = ref();
const discountType = ref<DiscountType | undefined>();
const renewReadableDate = computed(() => moment(planEndDate.value).format(DATE_FORMAT_DISPLAY));
const isPlanActive = computed(() => teamFeaturesSubscription.value.status === SubscriptionStatus.active);
const isUpgradeButtonDisabled = computed(() => isEqual(props.newPurchaseDetails, props.currentPurchaseDetails));
const hasTeamFeaturesBetaPlan = computed(() => {
	const plan = usePlanById().value(props.newPurchaseDetails.teamFeature.id);
	if (plan) {
		return BillingService.isPlanFreeBeta(plan);
	}
	return false;
});
const hasCustomerFeaturesBetaPlan = computed(() => {
	const plan = usePlanById().value(props.newPurchaseDetails.customerFeature.id);
	if (plan) {
		return BillingService.isPlanFreeBeta(plan);
	}
	return false;
});
const hasBetaPlans = computed(() => hasTeamFeaturesBetaPlan.value || hasCustomerFeaturesBetaPlan.value);

// Team features
const newTeamFeaturePlanId = computed(() => props.newPurchaseDetails.teamFeature.id);
const teamFeaturesPricePreviewInputParams = computed(() => ({
	planId: newTeamFeaturePlanId.value,
	billingPeriod: billingPeriod.value,
	productId: ProductType.TeamFeatures,
}));
// Customer features
const newCustomerFeaturePlanId = computed(() => props.newPurchaseDetails.customerFeature.id);
const newCustomerFeatureQuantity = computed(() => props.newPurchaseDetails.customerFeature.quantity);
const customerFeaturesPricePreviewInputParams = computed(() => ({
	billingPeriod: billingPeriod.value,
	planId: newCustomerFeaturePlanId.value,
	productId: ProductType.CustomerFeatures,
	unitQuantity: newCustomerFeatureQuantity.value,
}));
// Automation
const newAutomationQuantity = computed(() => props.newPurchaseDetails.automationTasks.quantity);
const automationPricePreviewInputParams = computed(() => ({
	productId: ProductType.Automation,
	billingPeriod: billingPeriod.value,
	unitQuantity: newAutomationQuantity.value,
}));

// Ply credits
const newPlyCreditsQuantity = computed(() => props.newPurchaseDetails.credits.quantity);
const plyCreditsPricePreviewInputParams = computed(() => ({
	productId: ProductType.Credits,
	billingPeriod: billingPeriod.value,
	unitQuantity: newPlyCreditsQuantity.value,
}));

// Prices
const loadingPrices = ref<Record<string, boolean>>({});
const isLoadingPrices = computed(() => Object.values(loadingPrices.value).some((loading) => loading));
const teamFeaturesPrices = computed(() => getProductPricePreview(teamFeaturesPricePreviewInputParams.value));
const customerFeaturesPrices = computed(() => getProductPricePreview(customerFeaturesPricePreviewInputParams.value));
const automationPrices = computed(() => getProductPricePreview(automationPricePreviewInputParams.value));
const plyCreditsPrices = computed(() => getProductPricePreview(plyCreditsPricePreviewInputParams.value));

// Checkout
const totalDueToday = computed(() => {
	const teamFeaturesDueToday = teamFeaturesPrices.value.dueToday;
	const customerFeaturesDueToday = customerFeaturesPrices.value.dueToday;
	const automationDueToday = automationPrices.value.dueToday;
	const plyCreditsDueToday = plyCreditsPrices.value.dueToday;
	return teamFeaturesDueToday + customerFeaturesDueToday + automationDueToday + plyCreditsDueToday;
});
const upComingInvoicePrice = computed(() => {
	const teamFeaturesUpComingInvoice = teamFeaturesPrices.value.upComingInvoice;
	const customerFeaturesUpComingInvoice = customerFeaturesPrices.value.upComingInvoice;
	const automationUpComingInvoice = automationPrices.value.upComingInvoice;
	const plyCreditsUpComingInvoice = plyCreditsPrices.value.upComingInvoice;
	return teamFeaturesUpComingInvoice + customerFeaturesUpComingInvoice + automationUpComingInvoice + plyCreditsUpComingInvoice;
});
const readableTotalDueToday = computed(() => getReadableAmountInDollars(totalDueToday.value));
const readableRenewableAmount = computed(() =>
	isEnterprisePlanSelected.value ? CUSTOM_PRICE : getReadableAmountInDollars(upComingInvoicePrice.value)
);
const upgradeButtonText = computed(() => {
	if (isEnterprisePlanSelected.value) {
		return 'Book a call';
	} else {
		return 'Continue';
	}
});

const summaryFields = computed(() =>
	[
		{ show: true, divider: true },
		{
			show: true,
			title: 'Workspace name',
			value: `${account.value.name}`,
		},
		{ show: true, divider: true },
		{
			show: true,
			title: 'Team features',
			isLoading: loadingPrices.value[buildLoadingPriceKey(teamFeaturesPricePreviewInputParams.value)],
			value: getProductPriceDisplay(
				isEnterprisePlanSelected.value,
				teamFeaturesPrices.value.upComingInvoice,
				discount.value,
				discountType.value
			),
		},
		{
			show: true,
			title: 'Customer features',
			isLoading: loadingPrices.value[buildLoadingPriceKey(customerFeaturesPricePreviewInputParams.value)],
			value: getProductPriceDisplay(
				isEnterprisePlanSelected.value,
				customerFeaturesPrices.value.upComingInvoice,
				discount.value,
				discountType.value
			),
		},
		{
			show: true,
			title: 'Automation',
			isLoading: loadingPrices.value[buildLoadingPriceKey(automationPricePreviewInputParams.value)],
			value: getProductPriceDisplay(
				isEnterprisePlanSelected.value,
				automationPrices.value.upComingInvoice,
				discount.value,
				discountType.value
			),
		},
		{
			show: true,
			title: 'Ply credits',
			isLoading: loadingPrices.value[buildLoadingPriceKey(plyCreditsPricePreviewInputParams.value)],
			value: getProductPriceDisplay(
				isEnterprisePlanSelected.value,
				plyCreditsPrices.value.upComingInvoice,
				discount.value,
				discountType.value
			),
		},
		{ show: true, divider: true },
		{ show: discount.value === 0, space: '8px' },
		{
			title: 'Discount',
			titleClass: 'renew-info',
			show: discount.value > 0,
			value: discountValue.value,
			valueClass: 'discount-value',
		},
		{
			title: 'Total due today',
			titleSuffix: SeeBreakdownButton,
			value: readableTotalDueToday.value,
			valuePrefixClass: 'line-through mr-1',
			valuePrefix: dueTodayBeforeDiscount.value,
			showValuePrefix: !!dueTodayBeforeDiscount.value,
			show: !isUpgradeButtonDisabled.value && !isEnterprisePlanSelected.value && !isLoadingPrices.value,
			titleSuffixProps: { newPurchaseDetails: props.newPurchaseDetails, currentPurchaseDetails: props.currentPurchaseDetails },
		},
	].filter((field) => field.show)
);

const getProductPriceDisplay = (isEnterprisePlanSelected: boolean, amount: number, discount: number, discountType?: DiscountType) =>
	isEnterprisePlanSelected ? CUSTOM_PRICE : getPreDiscountAmount(amount, discount, discountType) || getReadableAmountInDollars(amount);

const getPreDiscountAmount = (amount: number, discount: number, discountType?: DiscountType) => {
	if (discount === 0) {
		return null;
	}
	if (discountType === DiscountType.PERCENTAGE) {
		const oppositeFrictionToDiscount = 1 / ((100 - discount) / 100);
		return getReadableAmountInDollars(amount * oppositeFrictionToDiscount);
	}
	return getReadableAmountInDollars(amount - discount);
};

const dueTodayBeforeDiscount = computed(() => getPreDiscountAmount(totalDueToday.value, discount.value, discountType.value));

const totalRenewableAmountBeforeDiscount = computed(() =>
	getPreDiscountAmount(upComingInvoicePrice.value, discount.value, discountType.value)
);

const discountValue = computed(() => {
	if (discount.value > 0) {
		const discountValue = discount.value;
		if (discountType.value === DiscountType.PERCENTAGE) {
			return `${discountValue}%`;
		}
		return getReadableAmountInDollars(discountValue);
	}
	return null;
});

const togglePlanType = () => {
	isAnnuallyPlan.value = !isAnnuallyPlan.value;
};

const openUpgradeMultiplePlansDialog = () => {
	if (isEnterprisePlanSelected.value) {
		BillingService.bookACall({ user: user.value });
	} else {
		BillingService.openUpgradeMultiplePlansDialog({
			planEndDate: planEndDate.value,
			totalDueToday: readableTotalDueToday.value,
			newPurchaseDetails: props.newPurchaseDetails,
			renewableAmount: readableRenewableAmount.value,
			currentPurchaseDetails: props.currentPurchaseDetails,
		});
	}
};
const isEnterprisePlanSelected = computed(() => {
	const isTeamFeatureCustom = isPlanCustom(newTeamFeaturePlanId.value);
	const isCustomerFeatureCustom = isPlanCustom(newCustomerFeaturePlanId.value);
	let isAutomationCustom = false;
	const didAutomationQuantityChange = newAutomationQuantity.value !== props.currentPurchaseDetails.automationTasks.quantity;
	if (didAutomationQuantityChange) {
		isAutomationCustom = isNaN(newAutomationQuantity.value);
	} else {
		isAutomationCustom = BillingService.isEnterprisePlan(automationSubscription.value.plan);
	}
	let isPlyCreditsCustom = false;
	const didPlyCreditsQuantityChange = newPlyCreditsQuantity.value !== props.currentPurchaseDetails.credits.quantity;
	if (didPlyCreditsQuantityChange) {
		isPlyCreditsCustom = isNaN(newPlyCreditsQuantity.value);
	} else {
		isPlyCreditsCustom = BillingService.isEnterprisePlan(creditsSubscription.value.plan);
	}
	return isTeamFeatureCustom || isCustomerFeatureCustom || isAutomationCustom || isPlyCreditsCustom;
});

const isPlanCustom = (planId: string) => {
	const plan = usePlanById().value(planId);
	if (plan) {
		return BillingService.isEnterprisePlan(plan);
	}
	return false;
};
const buildLoadingPriceKey = ({ planId, productId, unitQuantity, billingPeriod }: PricePreviewInputParams) =>
	`${productId}-${planId}-${unitQuantity}-${billingPeriod}`;
const getPricePreview = async ({ planId, productId, unitQuantity, billingPeriod }: PricePreviewInputParams) => {
	if (showErrorMessage.value) {
		return;
	}
	if (planId && isPlanCustom(planId)) {
		return;
	}
	let res: ProductInvoicePreview = {} as ProductInvoicePreview;
	const priceKey = buildLoadingPriceKey({ planId, productId, unitQuantity, billingPeriod });
	try {
		loadingPrices.value = { ...loadingPrices.value, [priceKey]: true };
		res = await getBillingPreview({
			pricePreviewInput: BillingService.buildPricePreviewInput({ productId, planId, unitQuantity, billingPeriod }),
		});
		const subscriptionEndRange = res.preview_subscription.billing_period_range.end;
		if (subscriptionEndRange) {
			planEndDate.value = subscriptionEndRange;
		}
		const discountDetails = res.preview_subscription.recurring_invoice.discount_details;
		if (discountDetails) {
			const givenDiscount = discountDetails.value || 0;
			if (!discount.value && givenDiscount) {
				discount.value = givenDiscount;
				discountType.value = discountDetails.type;
			}
			return res;
		}
	} catch (e) {
		showErrorMessage.value = true;
	} finally {
		loadingPrices.value = { ...loadingPrices.value, [priceKey]: false };
	}
	return res;
};

const getProductPricePreview = (pricePreviewInputParams: PricePreviewInputParams) => {
	const pricePreview = usePricingPreview.value(BillingService.buildPricePreviewInput(pricePreviewInputParams));
	return BillingService.getProductPricePreview(pricePreview);
};
const getTeamFeaturesPrice = async () => getPricePreview(teamFeaturesPricePreviewInputParams.value);
const getCustomerFeaturesPrice = async () => {
	const unitQuantity = customerFeaturesPricePreviewInputParams.value.unitQuantity;
	const planId = customerFeaturesPricePreviewInputParams.value.planId;
	if (!isNullOrUndefined(planId) && !isNullOrUndefined(unitQuantity)) {
		const isFreePlan = customerFeaturesPricePreviewInputParams.value.planId.includes('free');
		if (isFreePlan || unitQuantity > 0) {
			return getPricePreview(customerFeaturesPricePreviewInputParams.value);
		}
	}
};
const getAutomationPrice = async () => {
	if (isNaN(automationPricePreviewInputParams.value.unitQuantity)) {
		return;
	}
	return getPricePreview(automationPricePreviewInputParams.value);
};
const getPlyCreditsPrice = async () => {
	if (isNaN(plyCreditsPricePreviewInputParams.value.unitQuantity)) {
		return;
	}
	return getPricePreview(plyCreditsPricePreviewInputParams.value);
};
watch(teamFeaturesPricePreviewInputParams, async (newV, oldV) => {
	if (newV && !isEqual(newV, oldV)) {
		getTeamFeaturesPrice();
	}
});
watch(customerFeaturesPricePreviewInputParams, async (newV, oldV) => {
	if (newV && !isEqual(newV, oldV)) {
		getCustomerFeaturesPrice();
	}
});
watch(automationPricePreviewInputParams, async (newV, oldV) => {
	if (newV && !isEqual(newV, oldV)) {
		getAutomationPrice();
	}
});
watch(plyCreditsPricePreviewInputParams, async (newV, oldV) => {
	if (newV && !isEqual(newV, oldV)) {
		getPlyCreditsPrice();
	}
});
const reloadData = () => {
	showErrorMessage.value = false;
	getTeamFeaturesPrice();
	getCustomerFeaturesPrice();
	getAutomationPrice();
	getPlyCreditsPrice();
};
watch(subscriptions, (newValue, oldValue) => {
	if (!isEmpty(newValue) && !isEmpty(oldValue) && !isEqual(newValue, oldValue)) {
		reloadData();
	}
});
watch(upComingInvoicePrice, (newValue, oldValue) => {
	if (!newValue && oldValue) {
		reloadData();
	}
});

return { __sfc: true,CUSTOM_PRICE, user, account, subscriptions, billingPeriod, isAnnuallyPlan, usePricingPreview, creditsSubscription, automationSubscription, teamFeaturesSubscription, usePlanById, getEntitlements, getBillingPreview, initializeData, props, showErrorMessage, discount, planEndDate, discountType, renewReadableDate, isPlanActive, isUpgradeButtonDisabled, hasTeamFeaturesBetaPlan, hasCustomerFeaturesBetaPlan, hasBetaPlans, newTeamFeaturePlanId, teamFeaturesPricePreviewInputParams, newCustomerFeaturePlanId, newCustomerFeatureQuantity, customerFeaturesPricePreviewInputParams, newAutomationQuantity, automationPricePreviewInputParams, newPlyCreditsQuantity, plyCreditsPricePreviewInputParams, loadingPrices, isLoadingPrices, teamFeaturesPrices, customerFeaturesPrices, automationPrices, plyCreditsPrices, totalDueToday, upComingInvoicePrice, readableTotalDueToday, readableRenewableAmount, upgradeButtonText, summaryFields, getProductPriceDisplay, getPreDiscountAmount, dueTodayBeforeDiscount, totalRenewableAmountBeforeDiscount, discountValue, togglePlanType, openUpgradeMultiplePlansDialog, isEnterprisePlanSelected, isPlanCustom, buildLoadingPriceKey, getPricePreview, getProductPricePreview, getTeamFeaturesPrice, getCustomerFeaturesPrice, getAutomationPrice, getPlyCreditsPrice, reloadData, BillingMonthlyAnnuallySwitch, BillingOveragesTooltip, ANNUAL_PLANS_DISCOUNT }
}

})